import { useEffect, useState } from "react";
import { Gauge, ListChecks, FileDown, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const riskColor = (risk) => (risk === "low" ? "#34745C" : risk === "medium" ? "#C48231" : "#B54D40");

export const CompliancePanel = () => {
  const { user } = useAuth();
  const canShare = user.role === "agency" || user.role === "admin";
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [shareClient, setShareClient] = useState("");
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    api.get("/compliance/score").then((res) => setData(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (shareOpen && clients.length === 0) {
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }
  }, [shareOpen, clients.length]);

  const shareWithFamily = async () => {
    if (!shareClient) return;
    setSharing(true);
    try {
      const { data: res } = await api.post(`/compliance/share/${shareClient}`);
      const name = clients.find((c) => c.id === shareClient)?.name || "the family";
      toast.success(`Compliance summary (score ${res.score}) sent to ${name}'s message thread`);
      setShareOpen(false);
      setShareClient("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not share the report");
    } finally {
      setSharing(false);
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get("/compliance/report.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nexari-compliance-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Compliance report downloaded");
    } catch {
      toast.error("Could not generate the report — please try again");
    } finally {
      setExporting(false);
    }
  };

  if (!data) return null;
  const color = riskColor(data.risk_level);

  return (
    <section data-testid="compliance-panel" className="bg-white border border-[#E8E6E1] rounded-xl p-6 md:p-8 fade-in-up">
      <div className="flex justify-end gap-2 -mb-2">
        {canShare && (
          <button
            data-testid="share-compliance-btn"
            onClick={() => setShareOpen(true)}
            className="flex items-center gap-2 text-xs font-semibold text-[#C07A60] border border-[#C07A60]/30 hover:bg-[#C07A60] hover:text-white transition-colors duration-200 rounded-full px-4 py-2"
          >
            <Share2 className="w-3.5 h-3.5" strokeWidth={2} /> Share with family
          </button>
        )}
        <button
          data-testid="export-compliance-pdf-btn"
          onClick={exportPdf}
          disabled={exporting}
          className="flex items-center gap-2 text-xs font-semibold text-[#2E5C50] border border-[#2E5C50]/30 hover:bg-[#2E5C50] hover:text-white transition-colors duration-200 rounded-full px-4 py-2 disabled:opacity-60"
        >
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2} /> : <FileDown className="w-3.5 h-3.5" strokeWidth={2} />}
          {exporting ? "Generating…" : "Export PDF report"}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-5">
          <div
            className="w-24 h-24 rounded-full flex flex-col items-center justify-center shrink-0"
            style={{ background: `conic-gradient(${color} ${data.score * 3.6}deg, #EFEDE8 0deg)` }}
          >
            <div className="w-[76px] h-[76px] bg-white rounded-full flex flex-col items-center justify-center">
              <span data-testid="compliance-score" className="text-2xl font-heading font-bold" style={{ color }}>
                {data.score}
              </span>
              <span className="text-[9px] uppercase tracking-wider text-[#57605A]">/ 100</span>
            </div>
          </div>
          <div>
            <h2 className="flex items-center gap-2 text-lg font-heading font-semibold">
              <Gauge className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.5} /> Compliance score
            </h2>
            <span
              data-testid="compliance-risk-badge"
              className="mt-1.5 inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider"
              style={{ backgroundColor: `${color}1A`, color }}
            >
              {data.risk_level} risk
            </span>
          </div>
        </div>

        <div className="flex-1 min-w-[260px] space-y-2.5">
          {data.dimensions.map((d) => (
            <div key={d.key} data-testid={`compliance-dimension-${d.key}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-[#1C211F]">
                  {d.label} <span className="text-[#57605A] font-normal">({d.weight}%)</span>
                </span>
                <span className="font-bold" style={{ color: riskColor(d.score >= 85 ? "low" : d.score >= 65 ? "medium" : "high") }}>
                  {d.score}
                </span>
              </div>
              <div className="mt-1 h-1.5 bg-[#EFEDE8] rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${d.score}%`, backgroundColor: riskColor(d.score >= 85 ? "low" : d.score >= 65 ? "medium" : "high") }}
                />
              </div>
              <p className="mt-0.5 text-[11px] text-[#57605A]">{d.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {data.required_fixes.length > 0 && (
        <div className="mt-6 border-t border-[#E8E6E1] pt-5">
          <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
            <ListChecks className="w-4 h-4" strokeWidth={1.5} /> Required fixes
          </h3>
          <ol data-testid="compliance-fixes" className="mt-3 space-y-2">
            {data.required_fixes.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                <span className="w-5 h-5 bg-[#2E5C50] text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                {f}
              </li>
            ))}
          </ol>
        </div>
      )}

      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent data-testid="share-compliance-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Share with family</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#57605A] leading-relaxed">
            Sends a simplified, client-specific compliance summary (their documents, visits and
            verification — no internal agency metrics) to the family's message thread.
          </p>
          <select
            data-testid="share-client-select"
            value={shareClient}
            onChange={(e) => setShareClient(e.target.value)}
            className="w-full bg-white border border-[#E8E6E1] rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#2E5C50]/30 focus:border-[#2E5C50]"
          >
            <option value="">Select a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            data-testid="share-compliance-send-btn"
            onClick={shareWithFamily}
            disabled={sharing || !shareClient}
            className="w-full flex items-center justify-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-6 py-2.5 text-sm font-medium disabled:opacity-50"
          >
            {sharing ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Share2 className="w-4 h-4" strokeWidth={1.5} />}
            {sharing ? "Sending…" : "Send summary"}
          </button>
        </DialogContent>
      </Dialog>
    </section>
  );
};
